import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
  importProvidersFrom,
  APP_INITIALIZER,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { HttpClientInMemoryWebApiModule } from 'angular-in-memory-web-api';
import { BidiModule } from '@angular/cdk/bidi';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpClient } from '@angular/common/http';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { getApp } from '@angular/fire/app';
import { getFunctions, provideFunctions } from '@angular/fire/functions';

import { routes } from './app.routes';
import { MockDataService } from './core/services/mock-data.service';
import { environment } from '../environments/environment';

import { RequestService } from './core/services/request.service';
import { InvoiceService } from './core/services/invoice.service';
import { AnnouncementService } from './core/services/announcement.service';
import { ClassifiedAdService } from './core/services/classified-ad.service';
import { DashboardService } from './core/services/dashboard.service';
import { ErrorHandlerService } from './core/services/error-handler.service';
import { LoadingService } from './core/services/loading.service';
import {
  AppInitializerService,
  initializeApp as initializeAppService,
} from './core/services/app-initializer.service';
import { FirebaseConfigService } from './core/services/firebase-config.service';
import { FirestoreService } from './core/services/firestore.service';
import { AuthService } from './core/services/auth.service';
import { ToastService } from './core/services/toast.service';
import { RequestDetailsService } from './core/services/request-details.service';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

/**
 * Initialize authentication before routing begins
 * Ensures auth state is determined before any route guards run
 */
export function initializeAuth(authService: AuthService) {
  return () => authService.waitForAuthInitialization();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),

    // Firebase providers
    provideFirebaseApp(() => {
      const firebaseConfigService = new FirebaseConfigService();
      const config = firebaseConfigService.getConfig();
      return initializeApp(config);
    }),
    provideFirestore(() => getFirestore()),
    provideAuth(() => getAuth()),
    provideStorage(() => getStorage()),
    provideMessaging(() => getMessaging()),
    provideFunctions(() => getFunctions(getApp(), 'europe-west1')),

    // Core services
    RequestService,
    InvoiceService,
    AnnouncementService,
    ClassifiedAdService,
    DashboardService,
    ErrorHandlerService,
    LoadingService,
    AppInitializerService,
    FirebaseConfigService,
    FirestoreService,
    AuthService,
    ToastService,
    RequestDetailsService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppService,
      deps: [AppInitializerService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      deps: [AuthService],
      multi: true,
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'en-US',
    },
    importProvidersFrom(
      BidiModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
        defaultLanguage: 'en',
        useDefaultLang: true,
      }),
      HttpClientInMemoryWebApiModule.forRoot(MockDataService, {
        dataEncapsulation: false,
        delay: 1000,
        passThruUnknownUrl: true,
        apiBase: '/api/',
      })
    ),
  ],
};
